import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  Camera,
  ImagePlus,
  Loader2,
  MonitorSmartphone,
  Paperclip,
  SendHorizontal,
  Sparkles,
  X,
} from "lucide-react";

import { cn, getFileUrl } from "@/lib/utils";
import useAuthStore from "@/store/use-auth-store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useImageAttachments } from "../hooks/use-image-attachments";
import { useScreenshotCaptureStore } from "../store/screenshot-capture-store";
import { collectDiagnostics } from "../utils/diagnostics";
import { isScreenCaptureSupported } from "../utils/screenshot";
import { buildMessageBody } from "../utils/ticket-context";
import type { DiagnosticsInfo } from "../utils/diagnostics";
import type { GenerationReference } from "../utils/ticket-context";
import { GenerationPickerDialog } from "./GenerationPickerDialog";

interface TicketReplyComposerProps {
  isReplying: boolean;
  onSend: (payload: { body: string; images: File[] }) => Promise<void>;
}

/** Ek olarak seçilen bağlamı gösteren, tek tıkla kaldırılabilen çip */
const ContextChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  detail?: string;
  thumbnailUrl?: string | null;
  onRemove: () => void;
}> = ({ icon, label, detail, thumbnailUrl, onRemove }) => (
  <div className="flex max-w-xs items-center gap-2 rounded-lg border border-border bg-card py-1.5 pl-2 pr-1.5">
    {thumbnailUrl ? (
      <img
        src={thumbnailUrl}
        alt=""
        className="h-7 w-7 shrink-0 rounded object-cover"
      />
    ) : (
      <span className="shrink-0 text-indigo-500">{icon}</span>
    )}
    <div className="flex min-w-0 flex-col">
      <span className="truncate text-xs font-medium text-foreground">
        {label}
      </span>
      {detail && (
        <span className="truncate text-[11px] text-muted-foreground">
          {detail}
        </span>
      )}
    </div>
    <button
      type="button"
      onClick={onRemove}
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <X className="h-3 w-3" />
    </button>
  </div>
);

/**
 * Cevap yazma alanı — "+" ek menüsü, sürükle-bırak, otomatik büyüyen alan
 * ve Enter ile gönderme (Shift+Enter yeni satır).
 */
export const TicketReplyComposer: React.FC<TicketReplyComposerProps> = ({
  isReplying,
  onSend,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    attachments,
    addAttachments,
    removeAttachment,
    clearAttachments,
    maxAttachments,
  } = useImageAttachments();

  const [body, setBody] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [diagnostics, setDiagnostics] = useState<DiagnosticsInfo | null>(null);
  const [generation, setGeneration] = useState<GenerationReference | null>(null);
  const [isGenerationPickerOpen, setIsGenerationPickerOpen] = useState(false);

  // Üretim referansı yalnızca kendi kütüphanesi olan oturumlarda anlamlı
  const { user } = useAuthStore();
  const isLoggedIn = !!user?._id;

  // Mobilde native ekran yakalama API'si yok; DOM taklidi güvenilmez
  // olduğundan bu cihazlarda seçenek hiç gösterilmez, kullanıcı native
  // ekran görüntüsünü galeriden ekler
  const canCaptureScreen = isScreenCaptureSupported();

  const location = useLocation();
  const startCapture = useScreenshotCaptureStore((state) => state.startCapture);
  const pendingCapture = useScreenshotCaptureStore(
    (state) => state.pendingFile,
  );
  const consumeCapture = useScreenshotCaptureStore(
    (state) => state.consumeCapture,
  );

  // Yakalama modundan dönüldüğünde görsel eklenir, taslak geri yüklenir
  useEffect(() => {
    if (!pendingCapture) return;
    const captured = consumeCapture();
    if (!captured) return;

    addAttachments([captured.file]);
    if (captured.draft?.body) setBody(captured.draft.body);
    // addAttachments her render'da yeniden üretiliyor; tetikleyici yalnızca
    // yeni yakalanan görsel olmalı
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCapture, consumeCapture]);

  // İçerik arttıkça alanı büyüt (max ~6 satır)
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [body]);

  const canSend = !!body.trim() && !isReplying;
  const canAttach = attachments.length < maxAttachments && !isReplying;

  const handleSend = async () => {
    if (!canSend) return;
    const composedBody = buildMessageBody(body, {
      diagnostics: diagnostics ?? undefined,
      generation: generation ?? undefined,
    });

    try {
      await onSend({ body: composedBody, images: attachments });
    } catch {
      // Hata bildirimini çağıran gösterir; taslak korunur
      return;
    }

    setBody("");
    clearAttachments();
    setDiagnostics(null);
    setGeneration(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    if (!canAttach) return;
    if (event.dataTransfer.files?.length) {
      addAttachments(event.dataTransfer.files);
    }
  };

  // Panodan görsel yapıştırma
  const handlePaste = (event: React.ClipboardEvent) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length > 0 && canAttach) {
      event.preventDefault();
      addAttachments(files);
    }
  };

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        if (canAttach) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        "relative shrink-0 border-t border-border bg-muted/30 px-3 py-2.5 transition-colors dark:bg-muted/10 sm:px-5 sm:py-3.5",
        isDragging && "bg-indigo-500/5 dark:bg-indigo-500/10",
      )}
    >
      {/* Sürükle-bırak katmanı */}
      {isDragging && (
        <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-indigo-500/60 bg-card/80 backdrop-blur-sm">
          <span className="flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400">
            <ImagePlus className="h-4 w-4" />
            {t("support.myTickets.dropHint")}
          </span>
        </div>
      )}

      {/* Seçili bağlam çipleri */}
      {(generation || diagnostics) && (
        <div className="mb-2.5 flex flex-wrap gap-2">
          {generation && (
            <ContextChip
              icon={<Sparkles className="h-4 w-4" />}
              label={generation.title}
              detail={generation.prompt || t("support.tools.generation")}
              thumbnailUrl={
                generation.type === "image"
                  ? getFileUrl(generation.fileUrl)
                  : null
              }
              onRemove={() => setGeneration(null)}
            />
          )}
          {diagnostics && (
            <ContextChip
              icon={<MonitorSmartphone className="h-4 w-4" />}
              label={t("support.tools.diagnostics")}
              detail={`${diagnostics.browser} · ${diagnostics.os}`}
              onRemove={() => setDiagnostics(null)}
            />
          )}
        </div>
      )}

      {/* Ek önizlemeleri */}
      {attachments.length > 0 && (
        <div className="mb-2.5 flex flex-wrap gap-2">
          {attachments.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="group relative h-16 w-16 overflow-hidden rounded-lg border border-border"
            >
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeAttachment(index)}
                className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition-opacity hover:bg-black/80"
                aria-label={t("common.delete")}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          if (event.target.files?.length) {
            addAttachments(event.target.files);
          }
          event.target.value = "";
        }}
      />

      {/* Giriş çubuğu */}
      <div className="flex items-end gap-2 rounded-2xl border border-border bg-card p-1.5 shadow-sm focus-within:border-indigo-500/50 focus-within:ring-2 focus-within:ring-indigo-500/10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isReplying}>
            <button
              type="button"
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors",
                isReplying
                  ? "cursor-not-allowed opacity-40"
                  : "hover:bg-muted hover:text-foreground",
              )}
              aria-label={t("support.myTickets.attachMenu")}
            >
              <Paperclip className="h-4.5 w-4.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="top"
            sideOffset={10}
            className={cn(
              "w-72 overflow-hidden rounded-2xl border-0 bg-popover p-2 text-popover-foreground",
              // Border yerine derin, yumuşak gölge — light/dark ayrımı
              "shadow-[0_8px_30px_-6px_rgba(15,23,42,0.18),0_2px_8px_-2px_rgba(15,23,42,0.08)]",
              "dark:bg-gray-900 dark:shadow-[0_12px_40px_-8px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.06)]",
            )}
          >
            {/* Soft muted hover — accent (light: mor / dark: gri) bu menüde
                çok satırlı öğeler ve renkli ikonlarla uyumsuz */}
            <DropdownMenuItem
              onClick={() => fileInputRef.current?.click()}
              disabled={!canAttach}
              className="gap-3 rounded-xl px-2.5 py-2.5 focus:bg-muted focus:text-foreground dark:focus:bg-white/5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 dark:bg-indigo-500/15">
                <ImagePlus className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">
                  {t("support.myTickets.attachImage")}
                </span>
              </span>
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground dark:bg-white/5">
                {attachments.length}/{maxAttachments}
              </span>
            </DropdownMenuItem>

            {canCaptureScreen && (
              <DropdownMenuItem
                onClick={() =>
                  startCapture({
                    returnTo: `${location.pathname}${location.search}`,
                    draft: { body },
                  })
                }
                disabled={!canAttach}
                className="gap-3 rounded-xl px-2.5 py-2.5 focus:bg-muted focus:text-foreground dark:focus:bg-white/5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 dark:bg-emerald-500/15">
                  <Camera className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">
                    {t("support.tools.captureScreen")}
                  </span>
                  <span className="text-xs leading-snug text-muted-foreground">
                    {t("support.tools.captureScreenHint")}
                  </span>
                </div>
              </DropdownMenuItem>
            )}

            {isLoggedIn && (
              <DropdownMenuItem
                // Diyalog, menü kapanma animasyonu bitince açılır; aksi hâlde
                // Radix'in odak iadesi ile diyalogun odak tuzağı çakışıyor
                onSelect={() =>
                  setTimeout(() => setIsGenerationPickerOpen(true), 0)
                }
                className="gap-3 rounded-xl px-2.5 py-2.5 focus:bg-muted focus:text-foreground dark:focus:bg-white/5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 dark:bg-violet-500/15">
                  <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">
                    {t("support.tools.attachGeneration")}
                  </span>
                  <span className="text-xs leading-snug text-muted-foreground">
                    {t("support.tools.attachGenerationHint")}
                  </span>
                </div>
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => setDiagnostics(collectDiagnostics())}
              disabled={!!diagnostics}
              className="gap-3 rounded-xl px-2.5 py-2.5 focus:bg-muted focus:text-foreground dark:focus:bg-white/5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 dark:bg-sky-500/15">
                <MonitorSmartphone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
              </span>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">
                  {t("support.tools.attachDiagnostics")}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {t("support.tools.attachDiagnosticsHint")}
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <textarea
          ref={textareaRef}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={t("support.myTickets.replyPlaceholder")}
          rows={1}
          disabled={isReplying}
          className="max-h-40 min-h-[36px] flex-1 resize-none bg-transparent py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-60"
        />

        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          aria-label={t("support.myTickets.replySend")}
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all",
            canSend
              ? "bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-sm hover:opacity-90 dark:from-indigo-500 dark:to-violet-500"
              : "cursor-not-allowed bg-muted text-muted-foreground",
          )}
        >
          {isReplying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <SendHorizontal className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Mobilde "Enter" ipucu anlamsız (sanal klavyede tutarsız davranır)
          ve dar ekranda gereksiz yer kaplıyor */}
      <p className="mt-1.5 hidden px-1 text-[11px] text-muted-foreground sm:block">
        {t("support.myTickets.composerHint")}
      </p>

      <GenerationPickerDialog
        open={isGenerationPickerOpen}
        onOpenChange={setIsGenerationPickerOpen}
        onSelect={setGeneration}
      />
    </div>
  );
};
